/* ============================================================================
   AWISH AUTH — Login / OTP / Session (DermaCo-style)
   ----------------------------------------------------------------------------
   This file is split into THREE clearly separated layers so the mock OTP flow
   can be replaced by a real backend (Firebase / MSG91 / Twilio / Shopify
   Customer Login) WITHOUT touching any UI code:

     1. PROVIDER   — sendOtp() / verifyOtp(). Swap this object only.
     2. AwishAuth  — session + public API used by the UI. Provider-agnostic.
     3. AwishLoginUI — modal, OTP inputs, profile-icon interception. UI only.

   To go live later:
     AwishAuth.setProvider(MyFirebaseProvider)   // implement send/verify
   ...and nothing else changes.
   ========================================================================== */
(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────────────────
     LAYER 1 — PROVIDER (mock). Replace this object to plug in a real backend.
     A provider must expose:
       sendOtp(phone)        -> Promise<{ success: boolean, message?: string }>
       verifyOtp(phone, code)-> Promise<boolean>
     ────────────────────────────────────────────────────────────────────── */
  var MOCK_OTP = '123456';

  var MockProvider = {
    name: 'mock',
    sendOtp: function (phone) {
      // Simulate network latency. No real SMS is sent.
      return new Promise(function (resolve) {
        setTimeout(function () {
          // In dev, surface the code so it can be tested without a phone.
          if (window.console) console.info('[AwishAuth] Mock OTP for ' + phone + ' is ' + MOCK_OTP);
          resolve({ success: true, message: 'OTP sent (mock).' });
        }, 600);
      });
    },
    verifyOtp: function (phone, code) {
      return new Promise(function (resolve) {
        setTimeout(function () { resolve(String(code) === MOCK_OTP); }, 400);
      });
    }
  };

  /* ──────────────────────────────────────────────────────────────────────
     LAYER 2 — AwishAuth: session management + provider-agnostic public API.
     ────────────────────────────────────────────────────────────────────── */
  var KEYS = { loggedIn: 'awishLoggedIn', phone: 'awishUserPhone' };
  var provider = MockProvider;

  var AwishAuth = {
    /* Swap the OTP backend without touching UI. */
    setProvider: function (p) { if (p && p.sendOtp && p.verifyOtp) provider = p; },
    getProviderName: function () { return provider.name || 'custom'; },

    isLoggedIn: function () {
      try { return localStorage.getItem(KEYS.loggedIn) === 'true'; }
      catch (e) { return false; }
    },
    getPhone: function () {
      try { return localStorage.getItem(KEYS.phone) || ''; }
      catch (e) { return ''; }
    },

    /* Send an OTP to the given (already-normalised) phone string. */
    sendOtp: function (phone) { return provider.sendOtp(phone); },

    /* Verify the OTP. On success the session is persisted. */
    verifyOtp: function (phone, code) {
      return provider.verifyOtp(phone, code).then(function (ok) {
        if (ok) {
          try {
            localStorage.setItem(KEYS.loggedIn, 'true');
            localStorage.setItem(KEYS.phone, phone);
          } catch (e) {}
        }
        return ok;
      });
    },

    logout: function () {
      try {
        localStorage.removeItem(KEYS.loggedIn);
        localStorage.removeItem(KEYS.phone);
      } catch (e) {}
    },

    /* "+919876543210" / "9876543210" -> "+91 98765 43210" for display. */
    formatPhone: function (raw) {
      var digits = String(raw || '').replace(/\D/g, '');
      if (digits.length > 10) digits = digits.slice(-10); // strip country code for grouping
      if (digits.length === 10) return '+91 ' + digits.slice(0, 5) + ' ' + digits.slice(5);
      return raw || '';
    }
  };

  window.AwishAuth = AwishAuth;

  /* ──────────────────────────────────────────────────────────────────────
     LAYER 3 — AwishLoginUI: modal behaviour, OTP inputs, icon interception.
     ────────────────────────────────────────────────────────────────────── */
  var PROFILE_URL = '/pages/profile';

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  var AwishLoginUI = {
    modal: null,
    currentPhone: '',
    resendTimer: null,

    init: function () {
      this.modal = qs('#awish-login-modal');
      this.bindGlobalTriggers();
      if (this.modal) this.bindModal();
    },

    /* Intercept every account/profile entry point so a guest NEVER reaches the
       Shopify hosted login page. Catches explicit triggers plus Dawn's account
       icons by class (desktop header + mobile menu drawer) as a safety net.
       Logged in -> profile page. Logged out -> open custom login modal. */
    bindGlobalTriggers: function () {
      var SELECTOR = [
        '[data-awish-profile-trigger]',
        '.header__icon--account',
        '.menu-drawer__account'
      ].join(',');
      document.addEventListener('click', function (e) {
        var trigger = e.target.closest(SELECTOR);
        if (!trigger) return;
        e.preventDefault();
        e.stopPropagation();
        if (AwishAuth.isLoggedIn()) {
          window.location.href = PROFILE_URL;
        } else {
          AwishLoginUI.open();
        }
      }, true); // capture phase: beat any other handlers/navigation
    },

    open: function () {
      if (!this.modal) { window.location.href = PROFILE_URL; return; }
      this.showView('login');
      this.modal.removeAttribute('hidden');
      document.body.classList.add('awish-modal-open');
      var input = qs('#awish-phone-input', this.modal);
      if (input) setTimeout(function () { input.focus(); }, 50);
    },

    close: function () {
      if (!this.modal) return;
      this.modal.setAttribute('hidden', '');
      document.body.classList.remove('awish-modal-open');
      this.clearResendTimer();
    },

    showView: function (view) {
      qsa('[data-awish-view]', this.modal).forEach(function (el) {
        el.hidden = el.getAttribute('data-awish-view') !== view;
      });
    },

    setError: function (scope, msg) {
      var el = qs('[data-awish-error="' + scope + '"]', this.modal);
      if (el) { el.textContent = msg || ''; el.hidden = !msg; }
    },

    bindModal: function () {
      var self = this;

      // Close interactions
      qsa('[data-awish-close]', this.modal).forEach(function (btn) {
        btn.addEventListener('click', function () { self.close(); });
      });
      this.modal.addEventListener('click', function (e) {
        if (e.target === self.modal || e.target.classList.contains('awish-login__overlay')) self.close();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !self.modal.hasAttribute('hidden')) self.close();
      });

      // ── Login form → request OTP ──
      var loginForm = qs('#awish-login-form', this.modal);
      loginForm.addEventListener('submit', function (e) {
        e.preventDefault();
        self.setError('login', '');
        var raw = qs('#awish-phone-input', self.modal).value.replace(/\D/g, '');
        if (raw.length !== 10) {
          self.setError('login', 'Please enter a valid 10-digit mobile number.');
          return;
        }
        self.currentPhone = '+91' + raw;
        var submitBtn = qs('#awish-login-submit', self.modal);
        self.setLoading(submitBtn, true);

        AwishAuth.sendOtp(self.currentPhone).then(function (res) {
          self.setLoading(submitBtn, false);
          if (res && res.success) {
            qs('[data-awish-otp-phone]', self.modal).textContent = AwishAuth.formatPhone(self.currentPhone);
            self.showView('otp');
            self.resetOtpInputs();
            self.startResendTimer();
            var first = qs('.awish-otp__box', self.modal);
            if (first) first.focus();
          } else {
            self.setError('login', (res && res.message) || 'Could not send OTP. Try again.');
          }
        });
      });

      // ── OTP boxes: auto-advance, backspace, paste ──
      this.bindOtpInputs();

      // ── OTP form → verify ──
      var otpForm = qs('#awish-otp-form', this.modal);
      otpForm.addEventListener('submit', function (e) {
        e.preventDefault();
        self.setError('otp', '');
        var code = self.collectOtp();
        if (code.length !== 6) { self.setError('otp', 'Enter all 6 digits.'); return; }
        var verifyBtn = qs('#awish-otp-verify', self.modal);
        self.setLoading(verifyBtn, true);

        AwishAuth.verifyOtp(self.currentPhone, code).then(function (ok) {
          self.setLoading(verifyBtn, false);
          if (ok) {
            window.location.href = PROFILE_URL;
          } else {
            self.setError('otp', 'Incorrect OTP. Please try again.');
            self.resetOtpInputs();
            var first = qs('.awish-otp__box', self.modal);
            if (first) first.focus();
          }
        });
      });

      // ── Resend ──
      qs('#awish-otp-resend', this.modal).addEventListener('click', function () {
        if (this.disabled) return;
        self.setError('otp', '');
        AwishAuth.sendOtp(self.currentPhone).then(function () {
          self.resetOtpInputs();
          self.startResendTimer();
          var first = qs('.awish-otp__box', self.modal);
          if (first) first.focus();
        });
      });

      // ── Change number link ──
      var changeLink = qs('#awish-otp-change', this.modal);
      if (changeLink) changeLink.addEventListener('click', function (e) {
        e.preventDefault();
        self.showView('login');
      });
    },

    bindOtpInputs: function () {
      var self = this;
      var boxes = qsa('.awish-otp__box', this.modal);
      boxes.forEach(function (box, i) {
        box.addEventListener('input', function () {
          this.value = this.value.replace(/\D/g, '').slice(0, 1);
          if (this.value && boxes[i + 1]) boxes[i + 1].focus();
        });
        box.addEventListener('keydown', function (e) {
          if (e.key === 'Backspace' && !this.value && boxes[i - 1]) boxes[i - 1].focus();
        });
        box.addEventListener('paste', function (e) {
          e.preventDefault();
          var data = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, 6);
          for (var j = 0; j < data.length && j < boxes.length; j++) boxes[j].value = data[j];
          var next = boxes[Math.min(data.length, boxes.length - 1)];
          if (next) next.focus();
        });
      });
    },

    collectOtp: function () {
      return qsa('.awish-otp__box', this.modal).map(function (b) { return b.value; }).join('');
    },
    resetOtpInputs: function () {
      qsa('.awish-otp__box', this.modal).forEach(function (b) { b.value = ''; });
    },

    startResendTimer: function () {
      var self = this;
      var btn = qs('#awish-otp-resend', this.modal);
      var label = qs('[data-awish-resend-timer]', this.modal);
      var seconds = 30;
      btn.disabled = true;
      this.clearResendTimer();
      var tick = function () {
        if (label) label.textContent = ' in ' + seconds + 's';
        if (seconds <= 0) {
          self.clearResendTimer();
          btn.disabled = false;
          if (label) label.textContent = '';
          return;
        }
        seconds--;
      };
      tick();
      this.resendTimer = setInterval(tick, 1000);
    },
    clearResendTimer: function () {
      if (this.resendTimer) { clearInterval(this.resendTimer); this.resendTimer = null; }
    },

    setLoading: function (btn, on) {
      if (!btn) return;
      btn.classList.toggle('is-loading', on);
      btn.disabled = on;
    }
  };

  window.AwishLoginUI = AwishLoginUI;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { AwishLoginUI.init(); });
  } else {
    AwishLoginUI.init();
  }
})();
